import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, IconChip, Skeleton, type Tone } from '@/components/ui';
import { cn, formatNumber } from '@/lib/utils';

/* גרפים קטנים ב-SVG/CSS, בלי ספריית גרפים — הנתונים כאן הם עשרות נקודות. */

/** מדד: אייקון צבעוני, תווית ומספר. */
export function Kpi({
  icon,
  tone,
  label,
  value,
  loading,
}: {
  icon: LucideIcon;
  tone: Tone;
  label: string;
  value: number | undefined;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3.5">
        <IconChip icon={icon} tone={tone} variant="solid" size="lg" />
        <div className="min-w-0">
          <p className="text-xs font-medium text-fg-muted">{label}</p>
          {loading || value === undefined ? (
            <Skeleton className="mt-1 h-6 w-12" />
          ) : (
            <p className="tabular text-2xl font-bold leading-(--leading-tight) text-fg">
              {formatNumber(value)}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * עמודות לאורך זמן. ציר הזמן משמאל לימין גם בעברית — כמו כל גרף זמן. כל עמודה
 * נושאת <title> (tooltip וקורא מסך), והגרף כולו מתואר ב-aria-label.
 */
export function TimeBars({
  points,
  label,
  describe,
  tone = 'accent',
  height = 96,
}: {
  points: Array<{ key: string; value: number; title: string }>;
  label: string;
  describe: (p: { key: string; value: number }) => string;
  tone?: 'accent' | 'violet' | 'teal';
  height?: number;
}) {
  const max = Math.max(1, ...points.map((p) => p.value));
  const w = 10;
  const fill = { accent: 'fill-accent', violet: 'fill-violet', teal: 'fill-teal' }[tone];
  return (
    <div dir="ltr">
      <svg
        viewBox={`0 0 ${points.length * w} ${height}`}
        preserveAspectRatio="none"
        className="block h-28 w-full"
        role="img"
        aria-label={label}
      >
        {points.map((p, i) => {
          const h = p.value === 0 ? 0 : Math.max(2, (p.value / max) * (height - 4));
          return (
            <rect
              key={p.key}
              x={i * w + 1.5}
              y={height - h}
              width={w - 3}
              height={h}
              rx={1.5}
              className={fill}
            >
              <title>{describe(p)}</title>
            </rect>
          );
        })}
        <line
          x1={0}
          x2={points.length * w}
          y1={height - 0.5}
          y2={height - 0.5}
          className="stroke-border"
          strokeWidth={1}
        />
      </svg>
      {points.length > 0 ? (
        <div className="mt-1 flex justify-between text-2xs text-fg-subtle tabular">
          <span>{points[0]!.title}</span>
          <span>{points[points.length - 1]!.title}</span>
        </div>
      ) : null}
    </div>
  );
}

/** רשימה עם פס יחסי — מסלולים, לחיצות, תחומים, אימוץ. */
export function BarList({
  items,
  max,
  format = formatNumber,
  tone = 'accent',
  empty,
}: {
  items: Array<{ key: string; label: ReactNode; value: number; suffix?: ReactNode }>;
  /** ברירת מחדל: הערך הגבוה ברשימה. לאחוזים — המכנה. */
  max?: number;
  format?: (n: number) => string;
  tone?: 'accent' | 'violet' | 'teal' | 'orange' | 'success';
  empty?: string;
}) {
  const { t } = useTranslation('admin');
  if (items.length === 0)
    return <p className="py-4 text-center text-xs text-fg-muted">{empty ?? t('usage.noData')}</p>;
  const top = Math.max(1, max ?? Math.max(...items.map((i) => i.value)));
  const bar = {
    accent: 'bg-accent',
    violet: 'bg-violet',
    teal: 'bg-teal',
    orange: 'bg-orange',
    success: 'bg-success',
  }[tone];
  return (
    <ul className="space-y-2.5">
      {items.map((item) => (
        <li key={item.key}>
          <div className="flex items-baseline justify-between gap-3 text-xs">
            <span className="min-w-0 truncate text-fg">{item.label}</span>
            <span className="shrink-0 tabular font-semibold text-fg">
              {/* bdi: שני מספרים צמודים (10 ו-100%) מתמזגים בכיוון RTL ל-"10100%" בלי בידוד. */}
              <bdi>{format(item.value)}</bdi>
              {item.suffix ? (
                <bdi className="ms-1.5 font-normal text-fg-subtle">({item.suffix})</bdi>
              ) : null}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-sunken" aria-hidden>
            <div
              className={cn('h-full rounded-full', bar)}
              style={{ width: `${Math.min(100, (item.value / top) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function percent(part: number, whole: number): string {
  if (whole <= 0) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}
